import { useState, useEffect } from 'react';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { initializeApp } from 'firebase/app';

// Configuration Firebase pour les notifications push
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
  databaseURL: process.env.REACT_APP_FIREBASE_DATABASE_URL
};

// Initialiser l'app Firebase pour les notifications
const firebaseApp = initializeApp(firebaseConfig, 'PushNotifications');

export const usePushNotifications = (db, currentUser, setPushNotifications) => {
  const [fcmToken, setFcmToken] = useState(null);
  const [permission, setPermission] = useState(Notification.permission);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Détecter iOS
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    setIsIOS(/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream);
  }, []);

  useEffect(() => {
    console.log('🔔 usePushNotifications - Début initialisation', { 
      currentUser: currentUser?.id, 
      db: !!db,
      permission,
      isIOS
    });

    if (!currentUser || !db) {
      console.log('🔔 usePushNotifications - Conditions non remplies');
      return;
    }

    const initializePushNotifications = async () => {
      try {
        console.log('🔔 usePushNotifications - Initialisation en cours...');
        
        // Vérifier si Firebase Messaging est supporté
        const messagingSupported = await isSupported();
        if (!messagingSupported) {
          console.log('🔔 Firebase Messaging non supporté sur cette plateforme');
          return;
        }

        const messaging = getMessaging(firebaseApp);
        
        // Vérifier si les notifications sont supportées
        if (!('Notification' in window)) {
          console.log('🔔 Notifications non supportées par ce navigateur');
          return;
        }

        console.log('🔔 Vérification permission actuelle:', permission);

        // Gestion spécifique iOS pour les permissions
        if (isIOS) {
          console.log('📱 iOS détecté - Configuration spécifique');
          
          // Sur iOS, les notifications push nécessitent HTTPS et un service worker
          if (window.location.protocol !== 'https:') {
            console.log('⚠️ iOS nécessite HTTPS pour les notifications push');
            return;
          }
        }

        // Demander la permission si pas déjà accordée
        if (permission === 'default') {
          console.log('🔔 Demande de permission...');
          const newPermission = await Notification.requestPermission();
          setPermission(newPermission);
          
          if (newPermission !== 'granted') {
            console.log('🔔 Permission de notifications refusée');
            return;
          }
          console.log('🔔 Permission accordée!');
        }

        if (permission === 'granted') {
          console.log('🔔 Permission déjà accordée, récupération token FCM...');
          
          // Configuration spécifique pour iOS
          const tokenOptions = {
            vapidKey: process.env.REACT_APP_VAPID_KEY,
            serviceWorkerRegistration: navigator.serviceWorker?.ready
          };

          // Récupérer le token FCM
          const token = await getToken(messaging, tokenOptions);
          
          console.log('🔔 Token FCM récupéré:', token ? 'OUI' : 'NON');
          
          if (token) {
            setFcmToken(token);
            console.log('🔔 Sauvegarde du token FCM pour user:', currentUser.userId);
            await saveFCMToken(db, currentUser.userId, token);
          }

          // Écouter les messages en foreground
          onMessage(messaging, (payload) => {
            console.log('🔔 Message reçu en foreground:', payload);
            
            // Ajouter la notification push au système de notifications
            if (payload.notification && setPushNotifications) {
              const pushNotification = {
                id: `push-${Date.now()}`,
                type: 'push',
                title: payload.notification.title,
                message: payload.notification.body,
                timestamp: new Date(),
                data: payload.data || {},
                isIOS: isIOS
              };
              
              console.log('🔔 Ajout notification push:', pushNotification);
              setPushNotifications(prev => [...prev, pushNotification]);
              
              // Afficher une notification toast si nécessaire
              if (payload.notification.title && payload.notification.body) {
                console.log('🔔 Notification push reçue:', payload.notification);
              }
            }
          });

          console.log('🔔 Notifications push initialisées avec succès!');
        }
      } catch (error) {
        console.error('🔔 Erreur initialisation notifications push:', error);
        
        // Gestion spécifique des erreurs iOS
        if (isIOS) {
          console.log('📱 Erreur iOS spécifique - Vérifier:');
          console.log('1. HTTPS activé');
          console.log('2. Service Worker enregistré');
          console.log('3. VAPID key configurée');
          console.log('4. Configuration Firebase correcte');
        }
      }
    };

    initializePushNotifications();
  }, [db, currentUser, permission, setPushNotifications, isIOS]);

  const saveFCMToken = async (db, userId, token) => {
    try {
      const userRef = doc(db, 'employees', userId);
      const userDoc = await getDoc(userRef);
      
      if (userDoc.exists()) {
        await setDoc(userRef, {
          fcmToken: token,
          fcmTokenUpdated: new Date()
        }, { merge: true });
      }
    } catch (error) {
      console.error('Erreur sauvegarde token FCM:', error);
    }
  };

  return { fcmToken, permission };
};
